import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { recordBehaviorEvent } from '../../util/api';

import { Heading, ListingCard, NamedLink } from '../../components';

import css from './SectionSimilarListingsMaybe.module.css';

const SKELETON_TILES = 4;

/**
 * Matterax find-similar: "Similar materials" rail at the bottom of a catalog
 * listing page. Entities come from the ListingPage duck's
 * fetchSimilarListings (entity store, server rank order), so these are real
 * ListingCards — save button, variant strip and category tag included.
 *
 * Mobile renders a snap-scrolling strip, desktop a 4-column grid (same
 * markup, the WhatsNewPage rail pattern). While the fetch is in flight a
 * skeleton row holds the space so a listing→listing navigation never shows
 * the previous listing's cards. Renders nothing when there is nothing to
 * show.
 *
 * "View all" hands the ranked ids to the search page (`/s?ids=`), which
 * preserves their order.
 */
const SectionSimilarListingsMaybe = props => {
  const { listings = [], inProgress = false, anchorId = null, source = null } = props;

  if (!inProgress && listings.length === 0) {
    return null;
  }

  const ids = listings.map(l => l.id.uuid);

  // Behaviour warehouse: which similar result got opened, at which rank —
  // the engagement half of the `similar` fetch event. Capture-phase so the
  // beacon fires before the card's link navigates.
  const recordRailClick = (clickedId, position, action) =>
    recordBehaviorEvent('similar_click', {
      listingId: clickedId,
      ...(position ? { position } : {}),
      context: {
        ...(anchorId ? { anchor: anchorId } : {}),
        ...(source ? { source } : {}),
        ...(action ? { action } : {}),
      },
    });

  return (
    <section className={css.root}>
      <div className={css.inner}>
        <div className={css.head}>
          <Heading as="h2" rootClassName={css.title}>
            <FormattedMessage
              id="ListingPage.similarListingsTitle"
              defaultMessage="Similar products"
            />
          </Heading>
          {!inProgress && ids.length > 3 ? (
            // span wrapper: NamedLink builds its <a> props from an allowlist,
            // so click handlers on it are silently dropped.
            <span onClickCapture={() => recordRailClick(null, null, 'view_all')}>
              <NamedLink
                name="SearchPage"
                to={{ search: `?ids=${ids.join(',')}` }}
                className={css.viewAllLink}
              >
                <FormattedMessage
                  id="ListingPage.similarListingsViewAll"
                  defaultMessage="View all"
                />
              </NamedLink>
            </span>
          ) : null}
        </div>
        <div className={css.railTrack}>
          {inProgress
            ? [...Array(SKELETON_TILES)].map((_, i) => (
                <div key={`skeleton-${i}`} className={css.railItem}>
                  <div className={css.skeletonImage} />
                  <div className={css.skeletonLine} />
                </div>
              ))
            : listings.map((l, i) => (
                <div
                  key={l.id.uuid}
                  className={css.railItem}
                  onClickCapture={() => recordRailClick(l.id.uuid, i + 1)}
                >
                  <ListingCard
                    listing={l}
                    renderSizes="(max-width: 549px) 72vw, (max-width: 767px) 46vw, (max-width: 1023px) 31vw, 300px"
                  />
                </div>
              ))}
        </div>
      </div>
    </section>
  );
};

export default SectionSimilarListingsMaybe;
